import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AspectValue,
  MaintainAndFocusDataType,
  MaintainAndFocusServicesWidgetData,
} from 'screens/WidgetGenerator/utils/misc';
import { MaintainAndFocusServices } from 'screens/WidgetGenerator/widgets/MaintainAndFocusServices/MaintainAndFocusServices';
import { useAsyncEffect } from 'customHooks/hooks';
import { logger } from 'utils/logger';
import { getSatisfactionBarChartData } from 'screens/WidgetGenerator/helpers/questionsScore';
import { WidgetGeneratorContext } from 'screens/WidgetGenerator/contexts/WidgetGeneratorContext';
import { LoaderMask } from 'components/LoaderMask/LoaderMask';
import { ReportBuilderComponentProps } from '../WidgetWrappers/WidgetWrappers';

const DEFAULT_SPLIT_POSITION = 7;

const EMPTY_MAINTAIN_AND_FOCUS_VALUES: MaintainAndFocusServicesWidgetData = {
  [MaintainAndFocusDataType.MAINTAIN]: [],
  [MaintainAndFocusDataType.FOCUS]: [],
};

export const MaintainAndFocusServicesHOC = (props: ReportBuilderComponentProps): ReactElement => {
  const { params } = props;
  const { filters } = params;
  const { t: translate } = useTranslation();
  const { client, cycles, selectedDateRange, selectedSurveyGroup } = useContext(WidgetGeneratorContext);
  const [maintainAndFocusSelectedData, setMaintainAndFocusSelectedData] = useState<MaintainAndFocusServicesWidgetData>(
    EMPTY_MAINTAIN_AND_FOCUS_VALUES,
  );

  const fetchMaintainAndFocusData = async () => {
    const fetchedSatisfactionData = await getSatisfactionBarChartData({
      client,
      cycles,
      selectedDateRange,
      selectedSurveyGroupId: selectedSurveyGroup.id,
    });

    return fetchedSatisfactionData.filter((question) => question.aspectValue === AspectValue.CHARACTERISTIC);
  };

  const {
    fetchedData: satisfactionData,
    isLoading,
    error,
  } = useAsyncEffect(fetchMaintainAndFocusData, [client, cycles, selectedDateRange]);
  if (error) {
    logger.error(error);
  }

  useEffect(() => {
    const shouldUpdateSelectedData = filters?.maintainAndFocusFilters && satisfactionData && !isLoading;
    if (shouldUpdateSelectedData) {
      setMaintainAndFocusSelectedData({
        focus: satisfactionData.filter((question) =>
          filters.maintainAndFocusFilters.focus.includes(question.genericQuestionId),
        ),
        maintain: satisfactionData.filter((question) =>
          filters.maintainAndFocusFilters.maintain.includes(question.genericQuestionId),
        ),
      });
    }
  }, [filters, satisfactionData, isLoading]);

  if (
    !maintainAndFocusSelectedData[MaintainAndFocusDataType.MAINTAIN]?.length &&
    !maintainAndFocusSelectedData[MaintainAndFocusDataType.FOCUS]?.length
  ) {
    return <p>{translate('widgetGenerator.widgets.maintainAndFocus.emptyState')}</p>;
  }

  return (
    <LoaderMask isLoading={isLoading}>
      <MaintainAndFocusServices
        widgetData={maintainAndFocusSelectedData}
        isShortBarChart
        isBenchmarkedServices
        splitPositionBarChart={DEFAULT_SPLIT_POSITION}
      />
    </LoaderMask>
  );
};
